import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { Icon } from '@iconify/react/dist/iconify.js';

// Define color palette as constants
const SUCCESS = {
  50: '#f0fdf4',
  100: '#dcfce7',
  200: '#bbf7d0',
  300: '#86efac',
  400: '#4ade80',
  500: '#22c55e',
  600: '#16a34a',
  700: '#15803d',
  800: '#166534',
  900: '#14532d',
};

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingTop: 20,
    paddingBottom: 40,
    paddingLeft: 20,
    paddingRight: 20,
    position: 'relative',
  },
  heading: {
    fontSize: 20,
    marginBottom: 5,
    marginTop: 10,
    textAlign: 'center',
    fontWeight: 'bold',
    color: SUCCESS[800],
  },
  section: {
    marginBottom: 10,
    fontSize: 12,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    marginVertical: 10,
  },
  dividerTop: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    marginVertical: 10,
  },
  plantationsCell: {
    flex: 1,
    textAlign: 'left',
    paddingLeft: 8,
    color: '#333',
    fontSize: 9,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: SUCCESS[50],
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: SUCCESS[500],
    paddingLeft: 8,
    backgroundColor: SUCCESS[100],
  },
  detailContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 5,
    marginTop: 5,
  },
  detailColumn: {
    width: '50%',
    paddingRight: 10,
    marginBottom:10
  },
  detailLabel: {
    fontWeight: '600',
    color: '#555',
    fontSize: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  headingInComLoop: {
    fontWeight: '600',
    color: '#555',
    fontSize: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  headingTextInComLoop: {
    fontSize: 10,
    color: '#555',
    fontWeight: '600',
    marginBottom: 10,
    marginLeft: 40,
  },
  detailValue: {
    flex: 1,
    fontSize: 10,
    color: '#333',
  },
  detailValueUnderline: {
    textDecorationLine: 'underline',
  },
  icon: {
    marginLeft: 4,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  mediaContainer: {
    flexDirection: 'column',
    marginTop: 5,
  },
  mediaItem: {
    width: '100%',
    marginBottom: 15,
    borderRadius: 4,
    overflow: 'hidden',
  },
  mediaText: {
    fontSize: 8,
    color: '#1e40af',
    textDecorationLine: 'underline',
  },
  emptyValue: {
    color: '#999',
    fontStyle: 'italic',
  },
  fullWidthRow: {
    width: '100%',
    marginTop: 6,
  },
  twoColumnTable: {
    width: '100%',
    borderWidth: 1,
    borderColor: SUCCESS[200],
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: SUCCESS[100],
  },
  tableLabel: {
    width: '40%',
    padding: 6,
    backgroundColor: SUCCESS[50],
    fontWeight: 'bold',
    fontSize: 9,
    color: SUCCESS[800],
    borderRightWidth: 1,
    borderRightColor: SUCCESS[200],
  },
  tableValue: {
    width: '60%',
    padding: 6,
    fontSize: 9,
    color: '#333',
  },
  personCard: {
    borderWidth: 1,
    borderColor: SUCCESS[200],
    borderRadius: 4,
    padding: 6,
    marginBottom: 12,
    backgroundColor: SUCCESS[50],
  },
  personCardChild: {
    width: 200,
    borderWidth: 1,
    borderColor: SUCCESS[200],
    borderRadius: 4,
    padding: 6,
    marginBottom: 6,
    backgroundColor: SUCCESS[50],
  },
  personName: {
    fontWeight: 'bold',
    fontSize: 9,
    color: SUCCESS[800],
    marginBottom: 2,
  },
  personDetail: {
    fontSize: 8,
    color: '#555',
    marginBottom: 2,
  },
  timelineContainer: {
    marginTop: 5,
  },
  timelineItem: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  timelineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: SUCCESS[500],
    marginRight: 6,
    marginTop: 3,
  },
  timelineContent: {
    flex: 1,
  },
  timelineDate: {
    fontSize: 8,
    color: '#666',
    marginBottom: 2,
  },
  timelineText: {
    fontSize: 9,
    color: '#333',
  },
  speciesTag: {
    backgroundColor: SUCCESS[100],
    borderColor: SUCCESS[300],
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    marginRight: 6,
    marginBottom: 6,
    fontSize: 8,
    color: SUCCESS[800],
  },
  followUpCard: {
    borderWidth: 1,
    borderColor: SUCCESS[200],
    borderRadius: 4,
    padding: 8,
    marginBottom: 12,
    backgroundColor: SUCCESS[50],
    minHeight: 100, // Ensure minimum height for better spacing
  },
  // imageWrapper: {
  //   width: '100%',
  //   marginBottom: 15,
  //   minHeight: 220, // Ensure minimum height to prevent cutting
  // },
  imageWrapper: {
    width: '100%',
    marginBottom: 15,
    minHeight: 220,
    // ✅ prevent cutting across pages
    breakInside: 'avoid',
    pageBreakInside: 'avoid',
  },

  imageStyle: {
    width: '100%', 
    height: 200,
    objectFit: 'contain',
    border: '1px solid #eee',
    padding: 4
  },
  imageCaption: {
    fontSize: 8, 
    textAlign: 'center', 
    marginTop: 4,
    color: '#666'
  },
  // New styles for better page management
  pageBreak: {
    marginTop: 20,
    break: 'before',
  },
  avoidPageBreak: {
    breakInside: 'avoid',
  },
  forcePageBreak: {
    break: 'before',
  }
,
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 0,
  },
  headerLogo: {
    width: 100,
    height: 'auto',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#14532d', // SUCCESS[900] tone
    textTransform: 'uppercase',
  },
  headerRight: {
    flex: 0.4,
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
  },
  headerQRCode: {
    width: 80,
    height: 80,
  },
  headerKey: {
    fontSize: 8,
    paddingRight: 10,
  },
  headerDate: {
    fontSize: 7,
    textAlign: 'center',
    marginBottom: 0,
    marginTop: 5,
    fontWeight: 'bold',
  },
});
const renderMedia = (mediaArray, label) => {
  if (!mediaArray || mediaArray.length === 0) return null;

  return (
    <View style={{ marginBottom: 15 }} break={false}>
      <View style={{ flexDirection: 'row', marginBottom: 5, marginTop: 10 }}>
        <Text style={styles.detailLabel}>{label}</Text>
      </View>
      
      <View style={styles.mediaContainer}>
        {mediaArray.map((media, index) => {
          // Extract base64 data from media object
          const mediaBase64 = (typeof media === 'object' && media.base64) ? media.base64 : null;
          
          if (!mediaBase64) {
            console.warn('Invalid media item - base64 data missing:', media);
            return null;
          }
          
          // const fileNameToShow = `Image ${index+1}`;

          return (
            <View key={index} style={styles.imageWrapper} wrap={false}>
              <Image src={mediaBase64} style={styles.imageStyle} />
              <Text style={styles.imageCaption}>Image {index + 1}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return 'No Data Found';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const renderTwoColumnTable = (data) => {
  return (
    <View style={styles.twoColumnTable}>
      {data.map((item, index) => (
        <View key={index} style={styles.tableRow}>
          <Text style={styles.tableLabel}>{item.label}</Text>
          <Text style={styles.tableValue}>{item.value || 'N/A'}</Text>
        </View>
      ))}
    </View>
  );
};

const PlantationPDF = ({ plantationData, qrCodeUrl }) => {
  // Prepare plantation location data
  const locationData = [
    { label: 'Province', value: plantationData.province },
    { label: 'District', value: plantationData.district },
    { label: 'Tehsil', value: plantationData.tehsil },
    { label: 'Coordinates', value: plantationData.location ? `Lat: ${plantationData.location.coordinates[1]}, Long: ${plantationData.location.coordinates[0]}` : 'N/A' },
  ];

  // Prepare plantation details data
  const plantationDetailsData = [
    { label: 'Plantation Key', value: plantationData.plantationKey },
    { label: 'Category', value: plantationData.category },
    { label: 'Year of Plantation', value: plantationData.yearOfPlantation },
    { label: 'Plantation Campaign', value: plantationData.plantationCampaign },
    { label: 'Plantation Type', value: plantationData.plantationType },
    { label: 'Number of Plants', value: plantationData.numberOfPlants },
    { label: 'Planted By', value: plantationData.plantedBy },
    { label: 'Plantation Date', value: formatDate(plantationData.plantationDate) },
    { label: 'Created At', value: formatDate(plantationData.createdAt) },
    { label: 'Last Updated', value: formatDate(plantationData.updatedAt) },
  ];

  // Render species tags
  const renderSpecies = () => {
    if (!plantationData.speciesName || plantationData.speciesName.length === 0) {
      return <Text style={styles.emptyValue}>No species specified</Text>;
    }

    return (
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginTop: 5 }}>
        {plantationData.speciesName.map((species, index) => (
          <Text key={index} style={styles.speciesTag}>
            {species}
          </Text>
        ))}
      </View>
    );
  };
// Render follow-up information with better page break handling
const renderFollowUps = () => {
  if (!plantationData.followUps || plantationData.followUps.length === 0) {
    return <Text style={styles.emptyValue}>No follow-ups recorded</Text>;
  }

  return plantationData.followUps.map((followUp, index) => (
    <View key={index} style={[styles.followUpCard, styles.avoidPageBreak]}>
      <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 5, color: SUCCESS[800] }}>
        Follow-up #{index + 1} - {formatDate(followUp.updatedAt)}
      </Text>
      <View style={styles.detailContainer}>
        <View style={styles.detailColumn}>
          <Text style={styles.detailLabel}>Average Height (cm)</Text>
          <Text style={styles.detailValue}>{followUp.averageHeight || 'N/A'}</Text>
        </View>
        <View style={styles.detailColumn}>
          <Text style={styles.detailLabel}>Survival Rate (%)</Text>
          <Text style={styles.detailValue}>{followUp.survivalRate || 'N/A'}</Text>
        </View>
      </View>
      <Text style={styles.detailLabel}>Remarks</Text>
      <Text style={styles.detailValue}>{followUp.remarks || 'No remarks provided'}</Text>
      
      {followUp.images && followUp.images.length > 0 && (
        <View style={{ marginTop: 10 }}>
          <Text style={styles.detailLabel}>Follow-up Images</Text>
          <View style={styles.mediaContainer}>
            {followUp.images.map((image, imgIndex) => {
              // Extract base64 data from image object - image is an object with url and base64 properties
              const imageBase64 = image?.base64;
              
              if (!imageBase64) {
                console.warn('Invalid follow-up image - base64 data missing:', image);
                return null;
              }

              return (
                <View key={imgIndex} style={styles.imageWrapper} wrap={false}>
                  <Image src={imageBase64} style={styles.imageStyle} />
                  <Text style={styles.imageCaption}>Follow-up No {index + 1} / Image No {imgIndex + 1}</Text>
                </View>

              );
            })}
          </View>
        </View>
      )}
    </View>
  ));
};

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header - Fixed on every page */}
       <View style={styles.headerContainer} fixed>
  {/* Left: Logo */}
  <Image
    src={process.env.PUBLIC_URL + '/assets/images/govpklogo.png'}
    style={styles.headerLogo}
  />

  {/* Center: Title */}
  <View style={styles.headerTitleContainer}>
    <Text style={styles.headerTitle}>
      PAKISTAN PLANTATION & GROWTH MONITORING SYSTEM
    </Text>
  </View>

  {/* Right: QR code + details */}
  <View style={styles.headerRight}>
    {qrCodeUrl && <Image src={qrCodeUrl} style={styles.headerQRCode} />}
    <Text style={styles.headerKey}>{plantationData.plantationKey}</Text>
    <Text style={styles.headerDate}>
      Generated Date:{' '}
      {new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      })}
    </Text>
  </View>
</View>

        
        <View style={styles.dividerTop} fixed />
        <Text style={styles.heading}>Plantation Details - {plantationData.plantationKey}</Text>

        {/* Location Information */}
        <Text style={styles.sectionTitle}>
          <Icon icon="mdi:map-marker" width={16} style={{ marginRight: 6 }} />
          Location Information
        </Text>
        {renderTwoColumnTable(locationData)}

        <View style={styles.divider} />
        
        {/* Plantation Details */}
        <Text style={styles.sectionTitle}>
          <Icon icon="mdi:tree" width={16} style={{ marginRight: 6 }} />
          Plantation Details
        </Text>
        {renderTwoColumnTable(plantationDetailsData)}

        {/* Species Information */}
        <View style={styles.twoColumnTable}>
          <View style={styles.tableRow}>
            <Text style={styles.tableLabel}>Species</Text>
            <View style={styles.tableValue}>
              {renderSpecies()}
            </View>
          </View>
        </View>

        {/* Public Remarks */}
        <View style={styles.twoColumnTable}>
          <View style={styles.tableRow}>
            <Text style={styles.tableLabel}>Remarks</Text>
            <Text style={styles.tableValue}>
              {plantationData.publicRemarks || 'No remarks provided'}
            </Text>
          </View>
        </View>

        {/* Plantation Images - Will flow to next page if needed */}
        {plantationData.images && plantationData.images.length > 0 && (
          <View style={styles.avoidPageBreak}>
            <Text style={styles.sectionTitle}>
              <Icon icon="mdi:image" width={16} style={{ marginRight: 6}} />
              Plantation Images
            </Text>
            {renderMedia(plantationData.images, '')}
          </View>
        )}
        
        {/* Follow-up Information - Force new page if needed */}
        <View style={styles.pageBreak}>
          <Text style={styles.sectionTitle}>
            <Icon icon="mdi:clipboard-list" width={16} style={{ marginRight: 6 }} />
            Follow-up Information
          </Text>
          {renderFollowUps()}
        </View>

        {/* Footer - Fixed on every page */}
        <View style={{ position: 'absolute', bottom: 10, left: 20, right: 20, flexDirection: 'column', alignItems: 'center' }} fixed>
          <View style={{ width: '100%', height: 1, backgroundColor: 'black', marginBottom: 5, marginTop: 10 }} />
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', width: '100%', fontSize: 6 }}>
            <Text>Forest Wildlife and Fisheries Department, Punjab, Pakistan | GIS Lab</Text>
            <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PlantationPDF;